import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, OnInit } from '@angular/core';
import { dateParser } from '../services/mask-date-parser';
import { timeParser } from '../services/time-parser.service';
import { ControlContainer, NgForm } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'cert-date-time-input',
  templateUrl: './date-time-input.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  styleUrl: './date-time-input.component.scss',
  standalone: false,
})
export class DateTimeInputComponent implements OnChanges, OnInit {
  @Input() model!: string | undefined;
  @Input() name = '';
  @Input() propertyName = '';
  @Input() isDisabled = false;

  modelDate: string | undefined;
  modelTime: string | undefined;
  modelAmPm: string | undefined;

  @Output() modelChange = new EventEmitter<string>();

  errorEmitter = new EventEmitter<any>();

  private modelChangeSubject = new Subject<void>();

  dateParser = dateParser;
  timeParser = timeParser;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['model'] && !changes['model'].firstChange) {
      this.setDefaultDateTime();
    }
  }

  ngOnInit() {
    this.setDefaultDateTime();

    this.modelChangeSubject.pipe(debounceTime(500)).subscribe(() => {
      this.emitModelChange();
    });
  }

  setDefaultDateTime() {
    setTimeout(() => {
      const sourceDate = this.model ? new Date(this.model) : new Date();
      const useDate = !isNaN(sourceDate.getTime()) ? sourceDate : new Date();

      const month = (useDate.getMonth() + 1).toString().padStart(2, '0');
      const day = useDate.getDate().toString().padStart(2, '0');
      const year = useDate.getFullYear();
      this.modelDate = `${month}/${day}/${year}`;

      const hours = useDate.getHours();
      const minutes = useDate.getMinutes().toString().padStart(2, '0');
      this.modelAmPm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = (((hours + 11) % 12) + 1).toString().padStart(2, '0'); // Convert 24h to 12h format
      this.modelTime = `${formattedHours}:${minutes}`;

      this.modelChange.emit(this.modelDate + ' ' + this.modelTime + ' ' + this.modelAmPm);
    });
  }

  modelChanged() {
    // Trigger the Subject to debounce model change
    this.modelChangeSubject.next();
  }

  emitModelChange() {
    if (!this.modelDate || !this.modelTime || !this.modelAmPm) {
      return;
    }

    if (this.modelDate.includes('T')) {
      // If modelDate is in ISO format (e.g., "2024-12-07T06:00:00.000Z"), convert it to "MM/DD/YYYY"
      const dateObj = new Date(this.modelDate);
      if (!isNaN(dateObj.getTime())) {
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        const day = dateObj.getDate().toString().padStart(2, '0');
        const year = dateObj.getFullYear();
        this.modelDate = `${month}/${day}/${year}`;
      } else {
        console.error('Invalid date format for modelDate.', {
          modelDate: this.modelDate,
        });
        return;
      }
    }

    // Handle date-only ISO format (YYYY-MM-DD) produced by the dateParser outputTransformFn
    if (/^\d{4}-\d{2}-\d{2}$/.test(this.modelDate)) {
      const [year, month, day] = this.modelDate.split('-');
      this.modelDate = `${month}/${day}/${year}`;
    }

    // Ensure modelDate is in the correct format
    const dateParts = this.modelDate.split('/');
    if (dateParts.length !== 3) {
      console.error('Invalid date format.', {
        modelDate: this.modelDate,
      });
      return;
    }

    // Parse the date components
    let [month, day, year] = dateParts;

    // Make sure each component is properly formatted
    if (!month || !day || !year) {
      console.error('Invalid date components.', {
        month,
        day,
        year,
      });
      return;
    }

    // Format each part with padStart to ensure it has the correct length
    month = month.padStart(2, '0');
    day = day.padStart(2, '0');

    // Ensure modelTime is formatted properly in "HH:MM" format
    let formattedModelTime = this.modelTime;
    if (!formattedModelTime.includes(':')) {
      // Assuming the input is like "933" for 9:33
      if (formattedModelTime.length === 3) {
        formattedModelTime = `0${formattedModelTime.substring(0, 1)}:${formattedModelTime.substring(1)}`;
      } else if (formattedModelTime.length === 4) {
        formattedModelTime = `${formattedModelTime.substring(0, 2)}:${formattedModelTime.substring(2)}`;
      } else {
        console.error('Invalid time format.', {
          modelTime: this.modelTime,
        });
        return;
      }
    }

    // Parse the time components
    let [hours, minutes] = formattedModelTime.split(':').map((num) => parseInt(num));
    if (isNaN(hours) || isNaN(minutes)) {
      console.error('Invalid time components.', {
        hours,
        minutes,
      });
      return;
    }

    // Handle AM/PM conversion properly
    if (this.modelAmPm === 'PM' && hours < 12) {
      hours += 12; // Convert PM times to 24-hour format
    } else if (this.modelAmPm === 'AM' && hours === 12) {
      hours = 0; // Handle 12 AM as midnight (00:00)
    }

    // Construct the combined date-time string in ISO 8601 format
    const combinedDateTime = `${year}-${month}-${day}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;

    // Parse the combined datetime to validate
    const parsedDate = new Date(combinedDateTime);

    if (!isNaN(parsedDate.getTime())) {
      // Emit the date-time string in ISO format (in UTC)
      const dateTimeString = parsedDate.toISOString();
      this.modelChange.emit(dateTimeString);
    } else {
      console.error('Invalid combined date or time format.', {
        combinedDateTime,
        modelDate: this.modelDate,
        modelTime: this.modelTime,
        modelAmPm: this.modelAmPm,
      });
    }
  }

  get dateName() {
    return `${this.name}-date-input`;
  }

  get timeName() {
    return `${this.name}-time-input`;
  }
}
